import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Pagination,
  Alert,
  Divider,
} from "@mui/material";

import {
  Users,
  GraduationCap,
  CheckCircle,
  XCircle,
  RefreshCcw,
  Search,
  Copy,
  Trash2,
  X,
  KeyRound,
} from "lucide-react";

const API_BASE = "https://api.sftech.ng";

// NOTE: confirm this matches whatever key your main admin login
// actually stores the JWT under.
function getAdminToken() {
  return localStorage.getItem("token");
}

async function apiFetch(path, options = {}) {
  const token = getAdminToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, data };
}

// navigator.clipboard.writeText() can reject (e.g. document not focused,
// insecure context, browser extension interference) — always handle that
// rejection so it never surfaces as an uncaught runtime error. Falls back
// to a manual selection method if the Clipboard API isn't usable at all.
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.warn("Clipboard API unavailable, falling back:", err);
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      return true;
    } catch (fallbackErr) {
      console.error("Copy failed entirely:", fallbackErr);
      return false;
    }
  }
}

const APPLICATION_STATUSES = ["payment_pending", "under_review", "converted"];
const PAYMENT_STATUSES = ["unpaid", "paid"];
const ADMISSION_STATUSES = ["pending", "approved", "rejected"];
const STUDENT_STATUSES = ["active", "suspended", "graduated", "withdrawn"];

const theme = {
  bg: "#050B14",
  panel: "#071B36",
  glass: "rgba(255,255,255,0.04)",
  stroke: "rgba(0,229,192,0.18)",
  teal: "#00e5c0",
  text: "#E6F1FF",
  red: "#ff6b6b",
  amber: "#ffb020",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: theme.text,
    bgcolor: theme.glass,
    borderRadius: 2,
    "& fieldset": { borderColor: theme.stroke },
    "&:hover fieldset": { borderColor: theme.teal },
    "&.Mui-focused fieldset": { borderColor: theme.teal },
  },
  "& .MuiInputLabel-root": { color: "#9FB3C8" },
  "& .MuiSelect-icon": { color: "#9FB3C8" },
};

function StatusChip({ value, tone = "default" }) {
  const tones = {
    default: { bg: "rgba(255,255,255,0.08)", color: "#9FB3C8" },
    teal: { bg: "rgba(0,229,192,0.15)", color: theme.teal },
    amber: { bg: "rgba(255,176,32,0.15)", color: theme.amber },
    red: { bg: "rgba(255,107,107,0.15)", color: theme.red },
  };
  const t = tones[tone];
  return (
    <Chip
      size="small"
      label={(value || "—").replace(/_/g, " ")}
      sx={{ bgcolor: t.bg, color: t.color, fontWeight: 800, textTransform: "capitalize" }}
    />
  );
}

function paymentTone(s) {
  return s === "paid" ? "teal" : "amber";
}
function admissionTone(s) {
  if (s === "approved") return "teal";
  if (s === "rejected") return "red";
  return "default";
}
function applicationTone(s) {
  if (s === "converted") return "teal";
  if (s === "under_review") return "amber";
  return "default";
}
function studentTone(s) {
  if (s === "active") return "teal";
  if (s === "suspended") return "red";
  if (s === "graduated") return "amber";
  return "default";
}

export default function AdminTrainingPage() {
  const [tab, setTab] = useState(0);

  // In-memory only — cleared on page refresh. The backend never stores
  // plaintext passwords (only a bcrypt hash), so this is purely a
  // convenience cache for the current browser session, not a persisted
  // record. Keyed by student id (uuid).
  const [recentPasswords, setRecentPasswords] = useState({});
  const rememberPassword = (studentId, password) => {
    setRecentPasswords((prev) => ({ ...prev, [studentId]: password }));
  };

  const [summary, setSummary] = useState({
    applicants: null,
    approved: null,
    rejected: null,
    students: null,
  });
  const [summaryLoading, setSummaryLoading] = useState(true);

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const [allApp, approvedApp, rejectedApp, activeStudents] = await Promise.all([
        apiFetch(`/api/training-applications?limit=1`),
        apiFetch(`/api/training-applications?limit=1&admission_status=approved`),
        apiFetch(`/api/training-applications?limit=1&admission_status=rejected`),
        apiFetch(`/api/training-students?limit=1&student_status=active`),
      ]);

      setSummary({
        applicants: allApp.data?.total ?? 0,
        approved: approvedApp.data?.total ?? 0,
        rejected: rejectedApp.data?.total ?? 0,
        students: activeStudents.data?.total ?? 0,
      });
    } catch (err) {
      console.error("Summary load error:", err);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: theme.bg, color: theme.text }}>
      {/* HEADER */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: theme.panel, borderBottom: `1px solid ${theme.stroke}` }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box>
            <Typography fontWeight={800} sx={{ letterSpacing: 0.5 }}>
              Training Control Center
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              Enterprise Applicant & Student Management System
            </Typography>
          </Box>

          <Button
            startIcon={<RefreshCcw size={16} />}
            onClick={loadSummary}
            sx={{ color: theme.teal, fontWeight: 700, textTransform: "none" }}
          >
            Sync System
          </Button>
        </Toolbar>
      </AppBar>

      {/* SUMMARY SECTION */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {[
            { label: "Applicants", value: summary.applicants, icon: <Users /> },
            { label: "Approved", value: summary.approved, icon: <CheckCircle /> },
            { label: "Rejected", value: summary.rejected, icon: <XCircle /> },
            { label: "Active Students", value: summary.students, icon: <GraduationCap /> },
          ].map((c) => (
            <Grid item xs={12} md={3} key={c.label}>
              <Card
                sx={{
                  bgcolor: theme.glass,
                  border: `1px solid ${theme.stroke}`,
                  backdropFilter: "blur(12px)",
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ opacity: 0.7, fontSize: 13 }}>{c.label}</Typography>
                    <Box sx={{ color: theme.teal }}>{c.icon}</Box>
                  </Box>

                  <Typography variant="h4" fontWeight={900} sx={{ mt: 1, color: theme.teal }}>
                    {summaryLoading ? (
                      <CircularProgress size={22} sx={{ color: theme.teal }} />
                    ) : (
                      c.value
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* TABS */}
        <Box sx={{ mt: 4 }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: theme.teal } }}
            sx={{
              "& button": { color: "#9FB3C8", fontWeight: 700, textTransform: "none" },
              "& .Mui-selected": { color: theme.teal },
            }}
          >
            <Tab label="Applicants" />
            <Tab label="Students" />
          </Tabs>
        </Box>

        {/* CONTENT PANEL */}
        <Box sx={{ mt: 3 }}>
          {tab === 0 ? (
            <ApplicantsSection onChanged={loadSummary} onPasswordIssued={rememberPassword} />
          ) : (
            <StudentsSection onChanged={loadSummary} recentPasswords={recentPasswords} onPasswordIssued={rememberPassword} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* ===================== APPLICANTS ===================== */
function ApplicantsSection({ onChanged, onPasswordIssued }) {
  const [applications, setApplications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 10;

  const [q, setQ] = useState("");
  const [applicationStatus, setApplicationStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [admissionStatus, setAdmissionStatus] = useState("");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [selected, setSelected] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [tempPasswordInfo, setTempPasswordInfo] = useState(null);
  const [copiedTemp, setCopiedTemp] = useState(false);

  const load = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = new URLSearchParams({ page, limit });
      if (q.trim()) params.set("q", q.trim());
      if (applicationStatus) params.set("application_status", applicationStatus);
      if (paymentStatus) params.set("payment_status", paymentStatus);
      if (admissionStatus) params.set("admission_status", admissionStatus);

      const { ok, data } = await apiFetch(`/api/training-applications?${params.toString()}`);
      if (!ok || !data.success) {
        setErrorMsg(data.message || "Failed to load applications.");
        return;
      }
      setApplications(data.applications || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
      setErrorMsg("Network error loading applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, applicationStatus, paymentStatus, admissionStatus]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const openReview = (application) => {
    setSelected(application);
    setReviewError("");
    setTempPasswordInfo(null);
  };

  const handleDecision = async (decision) => {
    if (!selected) return;
    setReviewLoading(true);
    setReviewError("");

    const { ok, data } = await apiFetch(`/api/training-applications/${selected.applicant_code}/review`, {
      method: "PATCH",
      body: JSON.stringify({ decision }),
    });

    setReviewLoading(false);

    if (!ok || !data.success) {
      setReviewError(data.message || "Could not update this application.");
      return;
    }

    if (decision === "approved" && data.temporary_password) {
      setTempPasswordInfo({ student_id: data.student?.student_id, password: data.temporary_password });
      if (data.student?.id) {
        onPasswordIssued?.(data.student.id, data.temporary_password);
      }
    } else {
      setSelected(null);
    }

    load();
    onChanged?.();
  };

  const handleDeleteApplicant = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete applicant ${selected.applicant_code}? This cannot be undone.`)) return;

    setReviewLoading(true);
    setReviewError("");

    const { ok, data } = await apiFetch(`/api/training-applications/${selected.applicant_code}`, {
      method: "DELETE",
    });

    setReviewLoading(false);

    if (!ok || !data.success) {
      setReviewError(data.message || "Could not delete this applicant.");
      return;
    }

    setSelected(null);
    load();
    onChanged?.();
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Box>
      <Box component="form" onSubmit={handleSearch} sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <TextField
          size="small"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, applicant code..."
          sx={{ ...fieldSx, minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color="#9FB3C8" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ ...fieldSx, minWidth: 170 }}>
          <InputLabel>Application</InputLabel>
          <Select
            label="Application"
            value={applicationStatus}
            onChange={(e) => { setApplicationStatus(e.target.value); setPage(1); }}
            MenuProps={{ PaperProps: { sx: { bgcolor: theme.panel, color: theme.text } } }}
          >
            <MenuItem value="">All</MenuItem>
            {APPLICATION_STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s.replace(/_/g, " ")}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ ...fieldSx, minWidth: 150 }}>
          <InputLabel>Payment</InputLabel>
          <Select
            label="Payment"
            value={paymentStatus}
            onChange={(e) => { setPaymentStatus(e.target.value); setPage(1); }}
            MenuProps={{ PaperProps: { sx: { bgcolor: theme.panel, color: theme.text } } }}
          >
            <MenuItem value="">All</MenuItem>
            {PAYMENT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ ...fieldSx, minWidth: 150 }}>
          <InputLabel>Admission</InputLabel>
          <Select
            label="Admission"
            value={admissionStatus}
            onChange={(e) => { setAdmissionStatus(e.target.value); setPage(1); }}
            MenuProps={{ PaperProps: { sx: { bgcolor: theme.panel, color: theme.text } } }}
          >
            <MenuItem value="">All</MenuItem>
            {ADMISSION_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          type="submit"
          sx={{ bgcolor: theme.teal, color: "#001b1a", fontWeight: 800, px: 3, "&:hover": { bgcolor: "#00c9a8" } }}
        >
          Search
        </Button>
      </Box>

      {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

      <Typography sx={{ mb: 2, opacity: 0.6 }}>Applicant pipeline overview</Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: theme.teal }} />
        </Box>
      ) : applications.length === 0 ? (
        <Typography sx={{ opacity: 0.5, textAlign: "center", py: 6 }}>No applications found.</Typography>
      ) : (
        applications.map((a) => (
          <Box
            key={a.applicant_code}
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 2,
              bgcolor: theme.glass,
              border: `1px solid ${theme.stroke}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography fontWeight={800}>{a.full_name}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {a.applicant_code} · {a.email}
              </Typography>
              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <StatusChip value={a.payment_status} tone={paymentTone(a.payment_status)} />
                <StatusChip value={a.admission_status} tone={admissionTone(a.admission_status)} />
                <StatusChip value={a.application_status} tone={applicationTone(a.application_status)} />
              </Box>
            </Box>

            <Chip
              label="Review"
              onClick={() => openReview(a)}
              sx={{ bgcolor: theme.teal, color: "#001b1a", fontWeight: 800, cursor: "pointer" }}
            />
          </Box>
        ))
      )}

      {!loading && applications.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
            sx={{
              "& .MuiPaginationItem-root": { color: "#9FB3C8" },
              "& .Mui-selected": { bgcolor: `${theme.teal}33 !important`, color: theme.teal },
            }}
          />
        </Box>
      )}

      {/* REVIEW DIALOG */}
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { bgcolor: theme.panel, color: theme.text, border: `1px solid ${theme.stroke}`, borderRadius: 3, minWidth: 420 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Review Applicant
          <IconButton onClick={() => setSelected(null)} sx={{ color: "#9FB3C8" }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: theme.stroke }}>
          {tempPasswordInfo ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Approved and converted to a student.
              </Alert>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Relay these credentials to the student directly — shown once and cannot be retrieved again.
              </Typography>
              <Box sx={{ bgcolor: theme.glass, borderRadius: 2, p: 2, border: `1px solid ${theme.stroke}` }}>
                <Typography variant="body2"><b>Student ID:</b> {tempPasswordInfo.student_id}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="body2"><b>Temp Password:</b> {tempPasswordInfo.password}</Typography>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const success = await copyToClipboard(tempPasswordInfo.password);
                      if (success) {
                        setCopiedTemp(true);
                        setTimeout(() => setCopiedTemp(false), 1500);
                      }
                    }}
                    sx={{ color: theme.teal }}
                  >
                    {copiedTemp ? <CheckCircle size={15} /> : <Copy size={15} />}
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : selected ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}><b>Name:</b> {selected.full_name}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><b>Email:</b> {selected.email}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><b>Phone:</b> {selected.phone}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><b>Program:</b> {selected.desired_program}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><b>Duration:</b> {selected.preferred_duration}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><b>Mode:</b> {selected.preferred_mode}</Typography>
              {selected.message && (
                <Typography variant="body2" sx={{ mb: 0.5 }}><b>Message:</b> {selected.message}</Typography>
              )}

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
                <StatusChip value={selected.payment_status} tone={paymentTone(selected.payment_status)} />
                <StatusChip value={selected.admission_status} tone={admissionTone(selected.admission_status)} />
                <StatusChip value={selected.application_status} tone={applicationTone(selected.application_status)} />
              </Box>

              {selected.payment_status !== "paid" && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This applicant hasn't paid yet — approval is blocked until payment is confirmed.
                </Alert>
              )}

              {reviewError && <Alert severity="error" sx={{ mt: 2 }}>{reviewError}</Alert>}

              {selected.application_status === "converted" && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This applicant has already been converted to a student.
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>

        {!tempPasswordInfo && selected && (
          <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
            <Button
              disabled={reviewLoading}
              onClick={handleDeleteApplicant}
              sx={{ color: theme.red, fontWeight: 700, textTransform: "none" }}
            >
              <Trash2 size={16} style={{ marginRight: 6 }} />
              Delete
            </Button>

            {selected.application_status !== "converted" && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  disabled={reviewLoading}
                  onClick={() => handleDecision("rejected")}
                  sx={{ color: theme.amber, fontWeight: 700, textTransform: "none" }}
                >
                  <XCircle size={16} style={{ marginRight: 6 }} />
                  Reject
                </Button>
                <Button
                  disabled={reviewLoading || selected.payment_status !== "paid"}
                  onClick={() => handleDecision("approved")}
                  sx={{
                    bgcolor: theme.teal,
                    color: "#001b1a",
                    fontWeight: 800,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#00c9a8" },
                    "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.08)", color: "#5a6b7a" },
                  }}
                >
                  <CheckCircle size={16} style={{ marginRight: 6 }} />
                  {reviewLoading ? "Working..." : "Approve"}
                </Button>
              </Box>
            )}
          </DialogActions>
        )}

        {tempPasswordInfo && (
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => { setSelected(null); setTempPasswordInfo(null); }}
              sx={{ bgcolor: theme.teal, color: "#001b1a", fontWeight: 800, textTransform: "none", width: "100%" }}
            >
              Done
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}

/* ===================== STUDENTS ===================== */
function StudentsSection({ onChanged, recentPasswords, onPasswordIssued }) {
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 10;

  const [q, setQ] = useState("");
  const [studentStatus, setStudentStatus] = useState("");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [resetPasswordInfo, setResetPasswordInfo] = useState(null);
  const [copiedReset, setCopiedReset] = useState(false);
  const [copiedRowId, setCopiedRowId] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    desired_program: "",
    preferred_duration: "",
    preferred_mode: "",
  });

  const load = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = new URLSearchParams({ page, limit });
      if (q.trim()) params.set("q", q.trim());
      if (studentStatus) params.set("student_status", studentStatus);

      const { ok, data } = await apiFetch(`/api/training-students?${params.toString()}`);
      if (!ok || !data.success) {
        setErrorMsg(data.message || "Failed to load students.");
        return;
      }
      setStudents(data.students || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
      setErrorMsg("Network error loading students.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, studentStatus]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const openStudent = (student) => {
    setSelected(student);
    setActionError("");
    setResetPasswordInfo(null);
    setEditMode(false);
    setEditForm({
      full_name: student.full_name || "",
      email: student.email || "",
      phone: student.phone || "",
      desired_program: student.desired_program || "",
      preferred_duration: student.preferred_duration || "",
      preferred_mode: student.preferred_mode || "",
    });
  };

  const handleEditChange = (e) => {
    setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveEdit = async () => {
    if (!selected) return;
    setActionLoading(true);
    setActionError("");

    const { ok, data } = await apiFetch(`/api/training-students/${selected.id}`, {
      method: "PATCH",
      body: JSON.stringify(editForm),
    });

    setActionLoading(false);

    if (!ok || !data.success) {
      setActionError(data.message || "Could not save changes.");
      return;
    }

    setSelected(data.student);
    setEditMode(false);
    load();
    onChanged?.();
  };

  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    setActionLoading(true);
    setActionError("");

    const { ok, data } = await apiFetch(`/api/training-students/${selected.id}`, {
      method: "PATCH",
      body: JSON.stringify({ student_status: newStatus }),
    });

    setActionLoading(false);

    if (!ok || !data.success) {
      setActionError(data.message || "Could not update status.");
      return;
    }

    setSelected(data.student);
    load();
    onChanged?.();
  };

  const handleResetPassword = async () => {
    if (!selected) return;
    setActionLoading(true);
    setActionError("");

    const { ok, data } = await apiFetch(`/api/training-students/${selected.id}/reset-password`, {
      method: "PATCH",
    });

    setActionLoading(false);

    if (!ok || !data.success) {
      setActionError(data.message || "Could not reset password.");
      return;
    }

    setResetPasswordInfo(data.temporary_password);
    onPasswordIssued?.(selected.id, data.temporary_password);
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete student ${selected.student_id}? This cannot be undone.`)) return;

    setActionLoading(true);
    setActionError("");

    const { ok, data } = await apiFetch(`/api/training-students/${selected.id}`, { method: "DELETE" });

    setActionLoading(false);

    if (!ok || !data.success) {
      setActionError(data.message || "Could not delete student.");
      return;
    }

    setSelected(null);
    load();
    onChanged?.();
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Box>
      <Box component="form" onSubmit={handleSearch} sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <TextField
          size="small"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, student ID..."
          sx={{ ...fieldSx, minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color="#9FB3C8" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ ...fieldSx, minWidth: 170 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={studentStatus}
            onChange={(e) => { setStudentStatus(e.target.value); setPage(1); }}
            MenuProps={{ PaperProps: { sx: { bgcolor: theme.panel, color: theme.text } } }}
          >
            <MenuItem value="">All</MenuItem>
            {STUDENT_STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          type="submit"
          sx={{ bgcolor: theme.teal, color: "#001b1a", fontWeight: 800, px: 3, "&:hover": { bgcolor: "#00c9a8" } }}
        >
          Search
        </Button>
      </Box>

      {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

      <Typography sx={{ mb: 2, opacity: 0.6 }}>Active student registry</Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: theme.teal }} />
        </Box>
      ) : students.length === 0 ? (
        <Typography sx={{ opacity: 0.5, textAlign: "center", py: 6 }}>No students found.</Typography>
      ) : (
        students.map((s) => (
          <Box
            key={s.id}
            onClick={() => openStudent(s)}
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 2,
              bgcolor: theme.glass,
              border: `1px solid ${theme.stroke}`,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              transition: "border-color 0.2s",
              "&:hover": { borderColor: theme.teal },
            }}
          >
            <Box>
              <Typography fontWeight={800}>{s.full_name}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {s.student_id} · {s.email}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {recentPasswords?.[s.id] && (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor: "rgba(0,229,192,0.1)",
                    border: `1px solid ${theme.stroke}`,
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.5,
                  }}
                  title="Only visible for this browser session — not retrievable after refresh"
                >
                  <Typography variant="caption" sx={{ color: theme.teal, fontWeight: 800 }}>
                    {recentPasswords[s.id]}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const success = await copyToClipboard(recentPasswords[s.id]);
                      if (success) {
                        setCopiedRowId(s.id);
                        setTimeout(() => setCopiedRowId(null), 1500);
                      }
                    }}
                    sx={{ color: theme.teal, p: 0.25 }}
                  >
                    {copiedRowId === s.id ? <CheckCircle size={13} /> : <Copy size={13} />}
                  </IconButton>
                </Box>
              )}
              <StatusChip value={s.student_status} tone={studentTone(s.student_status)} />
            </Box>
          </Box>
        ))
      )}

      {!loading && students.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
            sx={{
              "& .MuiPaginationItem-root": { color: "#9FB3C8" },
              "& .Mui-selected": { bgcolor: `${theme.teal}33 !important`, color: theme.teal },
            }}
          />
        </Box>
      )}

      {/* MANAGE STUDENT DIALOG */}
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { bgcolor: theme.panel, color: theme.text, border: `1px solid ${theme.stroke}`, borderRadius: 3, minWidth: 420 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Manage Student
          <IconButton onClick={() => setSelected(null)} sx={{ color: "#9FB3C8" }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: theme.stroke }}>
          {selected && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}><b>Student ID:</b> {selected.student_id}</Typography>

              {editMode ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                  <TextField
                    size="small"
                    label="Full Name"
                    name="full_name"
                    value={editForm.full_name}
                    onChange={handleEditChange}
                    sx={fieldSx}
                  />
                  <TextField
                    size="small"
                    label="Email"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditChange}
                    sx={fieldSx}
                  />
                  <TextField
                    size="small"
                    label="Phone"
                    name="phone"
                    value={editForm.phone}
                    onChange={handleEditChange}
                    sx={fieldSx}
                  />
                  <TextField
                    size="small"
                    label="Program"
                    name="desired_program"
                    value={editForm.desired_program}
                    onChange={handleEditChange}
                    sx={fieldSx}
                  />
                  <TextField
                    size="small"
                    label="Duration"
                    name="preferred_duration"
                    value={editForm.preferred_duration}
                    onChange={handleEditChange}
                    sx={fieldSx}
                  />
                  <TextField
                    size="small"
                    label="Mode"
                    name="preferred_mode"
                    value={editForm.preferred_mode}
                    onChange={handleEditChange}
                    sx={fieldSx}
                  />
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><b>Name:</b> {selected.full_name}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><b>Email:</b> {selected.email}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><b>Phone:</b> {selected.phone}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><b>Program:</b> {selected.desired_program}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><b>Duration:</b> {selected.preferred_duration}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><b>Mode:</b> {selected.preferred_mode}</Typography>
                </>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                <Typography variant="body2"><b>Status:</b></Typography>
                <StatusChip value={selected.student_status} tone={studentTone(selected.student_status)} />

                {!editMode && (
                  <Button
                    size="small"
                    onClick={() => setEditMode(true)}
                    sx={{ ml: "auto", color: theme.teal, fontWeight: 700, textTransform: "none" }}
                  >
                    Edit Info
                  </Button>
                )}
              </Box>

              {editMode && (
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button
                    disabled={actionLoading}
                    onClick={handleSaveEdit}
                    sx={{ bgcolor: theme.teal, color: "#001b1a", fontWeight: 800, textTransform: "none", flex: 1 }}
                  >
                    {actionLoading ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button
                    disabled={actionLoading}
                    onClick={() => setEditMode(false)}
                    sx={{ color: "#9FB3C8", fontWeight: 700, textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}

              {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}

              {!editMode && resetPasswordInfo ? (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>New temporary password issued.</Alert>
                  <Box sx={{ bgcolor: theme.glass, border: `1px solid ${theme.stroke}`, borderRadius: 2, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography fontWeight={800}>{resetPasswordInfo}</Typography>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        const success = await copyToClipboard(resetPasswordInfo);
                        if (success) {
                          setCopiedReset(true);
                          setTimeout(() => setCopiedReset(false), 1500);
                        }
                      }}
                      sx={{ color: theme.teal }}
                    >
                      {copiedReset ? <CheckCircle size={15} /> : <Copy size={15} />}
                    </IconButton>
                  </Box>
                </Box>
              ) : !editMode ? (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.stroke }} />
                  <Typography variant="caption" sx={{ opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Change Status
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                    {STUDENT_STATUSES.map((s) => (
                      <Chip
                        key={s}
                        label={s}
                        onClick={() => s !== selected.student_status && !actionLoading && handleStatusChange(s)}
                        sx={{
                          textTransform: "capitalize",
                          fontWeight: 700,
                          cursor: s === selected.student_status ? "default" : "pointer",
                          bgcolor: s === selected.student_status ? theme.teal : "rgba(255,255,255,0.08)",
                          color: s === selected.student_status ? "#001b1a" : "#9FB3C8",
                          opacity: actionLoading ? 0.5 : 1,
                        }}
                      />
                    ))}
                  </Box>
                </>
              ) : null}
            </>
          )}
        </DialogContent>

        {selected && !resetPasswordInfo && !editMode && (
          <DialogActions sx={{ p: 2 }}>
            <Button
              disabled={actionLoading}
              onClick={handleResetPassword}
              sx={{ color: theme.teal, fontWeight: 700, textTransform: "none" }}
            >
              <KeyRound size={16} style={{ marginRight: 6 }} />
              Reset Password
            </Button>
            <Button
              disabled={actionLoading}
              onClick={handleDelete}
              sx={{ color: theme.red, fontWeight: 700, textTransform: "none" }}
            >
              <Trash2 size={16} style={{ marginRight: 6 }} />
              Delete
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}
